import { Link, useLocation } from "react-router-dom";
import classes from "../components/loggedout/LoggedOut.module.scss";
import { useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { DDD } from "../lib/ddd/ddd";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import dddLogo from "../assets/logo.svg";
import { ThemeContext } from "../store/ThemeContext";
import LanguageSelector from "../components/UI/LanguageSelector";
import LoadingDots from "../components/UI/LoadingDots";
import PasswordStrengthBar from 'react-password-strength-bar';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useLoggedOutHeading } from "../store/LoggedOutHeaderContext";

const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";
const apiRequestAddressBase = process.env.REACT_APP_ORIGIN || window.location.origin;

const ChangePassword = () => {
    const { t, i18n } = useTranslation();
    useLoggedOutHeading(t("Password reset"));
    const location = useLocation();
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("init");//change,init,expired
    const [confirmId, setConfirmId] = useState();
    const [sessionInfo, setSessionInfo] = useState(null);
    const [showPassword1, setShowPassword1] = useState(false);
    const [showPassword2, setShowPassword2] = useState(false);
    const {logo} = useContext(ThemeContext);

    const splitRef = useRef(null);
    const headingRef = useRef(null);
    const logoRowRef = useRef(null);
    const nameLabelRef = useRef(null);
    const submitBtnRef = useRef(null);
    const formPanelRef = useRef(null);
    const debugModeRef = useRef(false);
    const [infoRowTops, setInfoRowTops] = useState(null);
    const [formMinHeight, setFormMinHeight] = useState(null);

    // "change" mode (name/email/username + both password fields) is by far the tallest of
    // this funnel's states — locks the form panel's height once while it's showing so
    // switching to init/expired/success doesn't visibly shrink the card. Same technique as
    // ResetPassword.jsx, just keyed off `mode` instead of `success`.
    useLayoutEffect(() => {
        if (mode !== "change") return;
        const panel = formPanelRef.current;
        if (!panel || (window.innerWidth <= 767 || window.innerHeight <= 600)) return;
        const measure = () => setFormMinHeight(panel.offsetHeight);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(panel);
        return () => ro.disconnect();
    }, [mode]);

    // Pins the left panel's logo group to the heading's top and the button to whichever
    // button/link is currently the form's primary action — same technique as
    // ResetPassword.jsx (see there for the detailed rationale).
    useLayoutEffect(() => {
        const split = splitRef.current;
        if (!split) return;

        const updateInfoRowTops = () => {
            if ((window.innerWidth <= 767 || window.innerHeight <= 600)) {
                setInfoRowTops(null);
                return;
            }
            const heading = headingRef.current;
            const submitBtn = submitBtnRef.current;
            const logoRow = logoRowRef.current;
            const nameLabel = nameLabelRef.current;
            if (!heading || !submitBtn) return;

            const nudgeDown = 8;
            let noAccountMarginTop;
            if (logoRow && nameLabel) {
                const logoBottom = heading.offsetTop + logoRow.offsetTop + logoRow.offsetHeight;
                noAccountMarginTop = nameLabel.offsetTop - logoBottom + nudgeDown;
            }

            // "change" mode's button sits naturally far enough down (after the read-only
            // rows and both password fields) to need no adjustment — same as
            // ResetPassword.jsx. "expired" is much shorter (heading + one line + button),
            // so its own button's top would place it right under the heading, overlapping
            // the left panel's tagline paragraph below it — clamped to a measured safe
            // minimum (paragraph bottom, ~254px from the panel top, plus a margin) instead.
            // Deliberately not measured against the card's own height/split.offsetHeight —
            // an earlier version did that and, combined with formMinHeight being locked
            // tall from a prior "change" mode visit, could push this number well past the
            // card's actual content, stranding the button in a large empty gap.
            const safeMinDistance = 280;
            const btnTop = Math.max(submitBtn.offsetTop, safeMinDistance);

            setInfoRowTops({
                groupTop: heading.offsetTop,
                btn: btnTop,
                noAccountMarginTop,
            });
        };

        updateInfoRowTops();
        if (document.fonts?.ready) {
            document.fonts.ready.then(updateInfoRowTops);
        }
        const ro = new ResizeObserver(updateInfoRowTops);
        ro.observe(split);
        window.addEventListener('resize', updateInfoRowTops);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateInfoRowTops);
        };
    }, [mode, success]);

    const {
        register,
        formState: { errors },
        handleSubmit,
        watch,
    } = useForm();
    const scoreWords = [t('weak'), t('weak'), t('okay'), t('good'), t('strong')];
    const password = watch('pass1');

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const id = query.get('id');
        if(!id)
            window.location = "@/dashboard-resetpassword";
        setConfirmId(id);
    }, [location]);

    // Activates the confirmation token automatically as soon as it's available, instead of
    // waiting on a "Continue" click — skips straight from the email link to the password
    // fields (or the expired message), with just a loading state in between.
    useEffect(() => {
        if(!confirmId || debugModeRef.current)
            return;
        onActivateChange();
    }, [confirmId]);

    // Debug-only: jumps straight to "change" mode with fake session data, bypassing the
    // real activation POST + session fetch — there's no way to reach this screen otherwise
    // without a genuine confirmation token from an actual reset-password email. Remove
    // before shipping.
    useEffect(() => {
        const handleKey = (e) => {
            if(e.key === 'F2'){
                debugModeRef.current = true;
                setSessionInfo({User: {Name: "John Doe", PrimaryUsername: "johndoe@yourcompany.com"}});
                setMode("change");
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, []);

    useEffect(() => {
        if(mode!== "change" || debugModeRef.current)
            return;

        const ddd = new DDD(apiRequestAddress);
        ddd.GetSessionAuthDescribe().then((response) => {
            setSessionInfo(response?.result)
        });
    }, [mode]);

    const resetSubmit = (data) => {
        setError(null);
        if(data.pass1 === "" && data.pass2 === "")
            return _setError(t("Please fill in at least one field"));

        if(data.pass1 !== data.pass2)
            return _setError(t("Passwords do not match"));

        if(!sessionInfo)
            return _setError(t("Session not found"));

        const ddd = new DDD(apiRequestAddress);
        ddd.ChangePassword(sessionInfo.User.PrimaryUsername, data.pass1, data.pass2).then((response) => {
            console.log(response);
            if(response?.result === "ok"){
                // Send them to login, not the dashboard: after a password change the old
                // session is no longer the one they just set credentials for, so landing on
                // the dashboard makes them find their own way to a login screen. Straight to
                // login lets them sign in with the new password immediately.
                //
                // `true`, not t(...): storing a translated string would freeze it at the
                // language active right now and stop following a later language switch (same
                // bug fixed in ResetPassword.jsx). Translation happens at render below.
                setSuccess(true);
                setTimeout(() => {
                    window.location = "@login";
                }, 2000);
            }
        }).catch((error) => {
            console.log(error);
            _setError(t("An error occurred while changing the password"));
        });
    };



    const _setError = (message) => {
        setError(message);
        setTimeout(() => {
            setError(null);
        }, 5000);
    }
    //https://dev.dddinvoices.com/confirmrequest?id=89e39546-87c6-4d2a-9bb8-d396b350f420
    const onActivateChange = () => {
        setLoading(true);
        axios({
            url:apiRequestAddressBase+"/confirmrequest?id="+confirmId+"&json=true",
            maxRedirects: 0,
            method: "post",
        }).then((response) => {
            setLoading(false);
            if(debugModeRef.current)
                return;
            if(response?.data?.Status !== "OK")
                setMode("expired");
            else
                setMode("change");
        }).catch((error) => {
            setLoading(false);
            if(debugModeRef.current)
                return;
            setMode("change");
            console.log(error);
        });
    }

    return (
        <div className={classes.loginScaleWrapper}>
        <div className={classes.loginSplit} ref={splitRef}>
            {!logo && <div className={classes.loginInfoPanel}>
                <div className={classes.loginInfoTopGroup} style={infoRowTops ? {top: infoRowTops.groupTop} : undefined}>
                    <div className={classes.loginInfoLogo} ref={logoRowRef}>
                        <img src={dddLogo} alt="" />
                        <span>DDD Invoices</span>
                    </div>
                    <div className={classes.loginInfoNoAccount} style={infoRowTops?.noAccountMarginTop !== undefined ? {marginTop: infoRowTops.noAccountMarginTop} : undefined}>
                        {t("Don't have an account?")}
                    </div>
                    <div className={classes.loginInfoBody}>
                        <p>{t("Sign up and start FREE 30 day access. Test e-invoicing, fiscalization, archiving and plugins.")}</p>
                    </div>
                </div>
                <button type="button" className={classes.loginInfoBtn} style={infoRowTops ? {top: infoRowTops.btn} : undefined} onClick={() => {window.location.href="@dashboard-sign-up";}}>{t("Start FREE")}</button>
            </div>}
            <div className={classes.loginFormPanel} ref={formPanelRef} style={{minHeight: formMinHeight || (mode !== "init" ? 480 : undefined)}}>
                <div className={classes.loginFlagWrap} style={{display:"flex", alignItems:"center"}}>
                    <LanguageSelector imgWidth="16"/>
                </div>
                <h2 className={classes.loginHeading} ref={headingRef}>{t("Password reset")}</h2>
                {!success && mode === "change" && <>
                    <div className={classes.changePasswordField}>
                        <label ref={nameLabelRef}>{t("Full Name")}</label>
                        <div className={classes.changePasswordValue}>{sessionInfo?.User?.Name}</div>
                    </div>
                    <div className={classes.changePasswordField}>
                        <label>{t("Email")}</label>
                        <div className={classes.changePasswordValue}>{sessionInfo?.User?.PrimaryUsername}</div>
                    </div>
                    <div className={classes.changePasswordField}>
                        <label>{t("Username")}</label>
                        <div className={classes.changePasswordValue}>{sessionInfo?.User?.PrimaryUsername}</div>
                    </div>
                    <form onSubmit={handleSubmit(resetSubmit)}>
                        <label htmlFor="pass1">{t("Create a password")}</label>
                        <div className={classes.loginPasswordWrap}>
                            <input name="pass1" id="pass1" className={classes.loginInput} placeholder={t("Must be 8 characters")} type={showPassword1 ? "text" : "password"} {...register("pass1", {required: t("This field is required")})}/>
                            <span className={classes.loginPasswordIcon} onClick={() => setShowPassword1(v => !v)}>
                                {!showPassword1 && <VisibilityIcon style={{fontSize:"1.1rem"}}/>}
                                {showPassword1 && <VisibilityOffIcon style={{fontSize:"1.1rem"}}/>}
                            </span>
                        </div>
                        {errors["pass1"] && <div style={{color: "rgb(232, 73, 14)"}}>{errors["pass1"].message}</div>}

                        <label htmlFor="pass2">{t("Confirm password")}*</label>
                        <div className={classes.loginPasswordWrap}>
                            <input name="pass2" id="pass2" className={classes.loginInput} placeholder={t("Repeat password")} type={showPassword2 ? "text" : "password"} {...register("pass2", {required: t("This field is required")})}/>
                            <span className={classes.loginPasswordIcon} onClick={() => setShowPassword2(v => !v)}>
                                {!showPassword2 && <VisibilityIcon style={{fontSize:"1.1rem"}}/>}
                                {showPassword2 && <VisibilityOffIcon style={{fontSize:"1.1rem"}}/>}
                            </span>
                        </div>
                        {errors["pass2"] && <div style={{color: "rgb(232, 73, 14)"}}>{errors["pass2"].message}</div>}

                        <PasswordStrengthBar password={password} minLength={8} scoreWords={scoreWords}/>
                        <div className={classes.changePasswordHint}>{t("Passwords are case sensitive. Both passwords must match.")}</div>

                        <div className={classes.errorSlot}>
                            {error && <div style={{color: "rgb(232, 73, 14)"}}>{error}</div>}
                        </div>
                        {loading && <LoadingDots/>}
                        {!loading && <button ref={submitBtnRef} type="submit" className={classes.loginSubmitBtn} disabled={loading}>{t("Change password")}</button>}
                    </form>
                </>}
                {!success && mode === "init" && <>
                    <LoadingDots/>
                </>}
                {!success && mode === "expired" && <>
                    <p className={classes.loginFormDescription}>{t("Confirmation token not found or expired. Did you perhaps follow an old email link?")}</p>
                    <Link ref={submitBtnRef} to="/login" className={classes.loginSubmitBtn} style={{marginTop: "auto", textAlign: "center", textDecoration: "none", boxSizing: "border-box"}}>{t("Back to login")}</Link>
                </>}
                {success && <>
                    <p className={classes.resetSuccessBody}>{t("Password changed successfully. Redirecting to login...")}</p>
                    <Link ref={submitBtnRef} to="/dashboard" className={classes.loginSubmitBtn} style={{marginTop: "auto", textAlign: "center", textDecoration: "none", boxSizing: "border-box"}}>{t("Continue")}</Link>
                </>}
            </div>
        </div>
        </div>
    );
}
export default ChangePassword;
